import {
  State as AuthState,
  selectCurrentEmail,
  selectCurrentPatientGroup,
  selectCurrentToken,
} from "../features/auth/authSlice";
import { Box, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";

import EmptyPage from "../../assets/app_hero.png";
import LoginButton from "../components/LoginButton";
import React from "react";
import { createStyleSheet } from "../utils/styles";
import { logEvent } from "../features/api/apiSlice";
import { setCredentials } from "../features/auth/authSlice";
import { theme } from "../../theme";
import { useAppDispatch } from "../features/store";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

const getGoogleClientID = () => {
  const host = window.location.hostname;
  const result = host.includes(".staging.") || host === "localhost";
  const clientId = result
    ? "12563075976-d6un192skhooj0cm85o9h6cbefifrpvu.apps.googleusercontent.com"
    : "41723139785-0641qoagcfpmiskspn57s8m17818gks3.apps.googleusercontent.com";

  return clientId;
};

const getMicrosoftClientID = () => {
  const host = window.location.hostname;
  const isStaging = host.includes(".staging.") || host === "localhost";
  return isStaging
    ? "1b77fccd-2434-431b-b687-dc9c1f10b9a5"
    : "8470272e-43ec-479f-81e1-72cc4cda40ea";
};

const Login = () => {
  const [errorMsg, setErrorMsg] = useState("");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation("Login");
  const isLoggedIn = !!useSelector(selectCurrentToken);
  const userEmail = useSelector(selectCurrentEmail);
  const patientGroup = useSelector(selectCurrentPatientGroup);

  const loginSuccess = useCallback(
    (authData: AuthState) => {
      dispatch(setCredentials(authData));
      logEvent(dispatch, {
        event: "userLogin",
        data: { success: true },
      });
    },
    [dispatch]
  );
  const loginError = useCallback(
    (errorMsg: string) => {
      logEvent(dispatch, {
        event: "userLogin",
        data: { success: false, errorMsg },
      });
      setErrorMsg(errorMsg);
      console.error("Login Failed: " + errorMsg);
    },
    [setErrorMsg, dispatch]
  );

  useEffect(() => {
    if (isLoggedIn && !!userEmail && !!patientGroup?.[userEmail]) {
      navigate("/patients");
    }
  }, [isLoggedIn, userEmail, patientGroup, navigate]);

  return (
    <Box sx={styles.container}>
      <Box sx={styles.leftPane}>
        <Typography variant="h3" sx={styles.welcomeText}>
          {t("welcome")}
        </Typography>
        <LoginButton
          googleClientId={getGoogleClientID()}
          microsoftClientId={getMicrosoftClientID()}
          onAuthSuccess={loginSuccess}
          onAuthError={loginError}
          sx={{ marginTop: 8 }}
        />
        <Typography variant="errorBody" sx={{ marginTop: 4 }}>
          {errorMsg}
        </Typography>
      </Box>
      <Box sx={styles.rightPane}>
        <Box sx={styles.heroContainer}>
          <Box
            component="img"
            src={EmptyPage}
            sx={styles.heroImage}
            alt="Logo"
          />
        </Box>
      </Box>
    </Box>
  );
};

const styles = createStyleSheet({
  welcomeText: {
    textAlign: "center",
  },
  container: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  leftPane: {
    height: "100%",
    width: (theme) => theme.spacing(56),
    [theme.breakpoints.down("desktop")]: {
      width: (theme) => theme.spacing(40),
    },
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    p: 4,
  },
  rightPane: {
    backgroundColor: (theme) => theme.palette.detailBackground.main,
    height: "100%",
    flex: 1,
    display: "flex",
    alignItems: "center",
  },
  heroContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  heroImage: {
    maxWidth: "50%",
    maxHeight: "50%",
    aspectRatio: 1.16,
  },
  welcomePromptText1: {
    marginTop: 4,
    width: "60%",
  },
  welcomePromptText2: {
    marginTop: 2,
    width: "60%",
  },
});

export default Login;
